'use client';

import '@/core/services/eva/load';
import {
  type EvaEndpoint,
  setServiceSetting,
} from '@springtree/eva-sdk-core-service';
import { setCoreSetting } from '@springtree/eva-sdk-core-settings';
import { useIsMutating } from '@tanstack/react-query';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import type { OrganizationUnit } from '@/core/queries/eva/use-organizations-query';
import useOrder from '@/core/hooks/use-order';
import { usePathname } from '@/core/i18n/routing';
import { MUTATION_KEY as UPDATE_BILLING_ADDRESS_MUTATION_KEY } from '@/core/mutations/bff/use-bff-update-cart-billing-address-mutation';
import { MUTATION_KEY as UPDATE_SHIPPING_ADDRESS_MUTATION_KEY } from '@/core/mutations/bff/use-bff-update-cart-shipping-address-mutation';
import { addRequestInterceptor } from '@/core/services/eva/call-eva-service';
import getEndpoint from '@/core/services/eva/get-endpoint';
import { hasCheckoutCartSnapshot } from '@/core/utils/checkout-cart-snapshot';
import {
  clearCheckoutMarketSession,
  getStoredCheckoutMarketCountry,
  setStoredCheckoutMarketCountry,
  shouldDropCartForMarketSwitch,
} from '@/core/utils/clear-checkout-market-session';
import useCookieStorageState from '@/ui/hooks/use-cookie-storage-state';
import useLocaleParams from '@/ui/hooks/use-locale-params';
import { useCookieStorageContext } from './cookie-storage';

export type BaseConfiguration = Record<string, unknown> & {
  ContentCultures?: {
    CountryID: string;
    LanguageID: string;
  }[];
  OrganizationUnitCulture?: {
    CountryID: string;
    LanguageID: string;
  };
};

export type Context = {
  endpoint: EvaEndpoint;
  configuration: BaseConfiguration;
  organization: OrganizationUnit | null;
  orderId: string | null;
  setOrderId: (id: string | null) => void;
  authenticationToken: string | null;
  setAuthenticationToken: (token: string | null) => void;
};

export const EvaContext = createContext<Context | null>(null);

export const useEvaContext = () => {
  const context = useContext(EvaContext);
  if (!context) throw Error('EvaContext not found');
  return context;
};

type EvaProviderProps = {
  configuration: BaseConfiguration;
  authenticationToken: string | null;
  organization: OrganizationUnit | null;
};

const OrderHandler = () => {
  const cookieStorage = useCookieStorageContext();
  const pathname = usePathname();
  const { cart, orderId, clearOrder } = useOrder();
  const { organization } = useEvaContext();

  // For non-EVA countries, skip order management
  const isNonEvaCountry = !organization;
  const isOnCheckout = pathname.includes('/checkout');

  // Address PUTs are in flight while the checkout delivery form syncs billing/shipping.
  // `useIsMutating` matches the mutation keys by prefix, so it stays true for the active cart.
  const isAddressSyncInFlight =
    useIsMutating({ mutationKey: UPDATE_BILLING_ADDRESS_MUTATION_KEY }) +
      useIsMutating({ mutationKey: UPDATE_SHIPPING_ADDRESS_MUTATION_KEY }) >
    0;

  // Carts are created lazily on first add-to-cart (see useOrder.runWithStaleCartRecovery).

  /**
   * Drop stale order id only when the cart is cancelled and empty outside checkout.
   * The BFF also returns `Confirmed` once checkout prerequisites are met (e.g. addresses set);
   * that must NOT clear `orderId`, or we create an empty cart and lose line items (see checkout
   * delivery submit flow). After a cancelled iDEAL payment the BFF may briefly report `Cancelled`
   * with no line items — never discard the cart id on checkout so the shopper can retry payment.
   */
  useEffect(() => {
    if (isOnCheckout) return;
    if (cart?.status !== 'Cancelled') return;

    const hasLineItems =
      cart.products.length > 0 || cart.subscriptions.length > 0;
    if (!hasLineItems) {
      clearOrder();
    }
  }, [cart, clearOrder, isOnCheckout]);

  // Cart updates are fetched explicitly after mutations; no EVA SignalR subscription needed.

  // Keep lightweight cart item-count cookie in sync for proxy route guard (skip for non-EVA countries)
  useEffect(() => {
    if (!cart || isNonEvaCountry) return;
    const itemsCount =
      cart.products.reduce((sum, p) => sum + p.quantity, 0) +
      cart.subscriptions.reduce((sum, s) => sum + s.quantity, 0);
    // After a failed redirect payment the BFF can briefly return an empty cart while we
    // restore line items from the pre-payment snapshot — keep the cookie count until then.
    if (isOnCheckout && itemsCount === 0 && hasCheckoutCartSnapshot()) {
      return;
    }
    if (typeof itemsCount === 'number') {
      cookieStorage.set('cartItemsCount', itemsCount).catch(console.error);
    }
  }, [cart, cookieStorage, isNonEvaCountry, isOnCheckout]);

  /** Drop cart from a previous market when the EVA organization country changes (e.g. en-gb → en-nl). */
  useEffect(() => {
    if (isNonEvaCountry || !organization?.countryId) return;
    // Never drop the cart mid address-sync on checkout: the delivery form is actively writing
    // billing/shipping addresses, and dropping the order here would race that write (latent F1).
    if (isOnCheckout && isAddressSyncInFlight) return;

    const organizationCountry = organization.countryId.toUpperCase();
    const hasLineItems =
      (cart?.products.length ?? 0) > 0 || (cart?.subscriptions.length ?? 0) > 0;

    if (
      shouldDropCartForMarketSwitch({
        orderId,
        organizationCountryId: organizationCountry,
        storedMarketCountry: getStoredCheckoutMarketCountry(),
        shippingAddressCountryCode: cart?.shippingAddress?.countryCode,
        billingAddressCountryCode: cart?.billingAddress?.countryCode,
        hasLineItems,
      })
    ) {
      clearCheckoutMarketSession();
      clearOrder();
    }

    setStoredCheckoutMarketCountry(organizationCountry);
  }, [
    cart,
    clearOrder,
    isAddressSyncInFlight,
    isNonEvaCountry,
    isOnCheckout,
    orderId,
    organization,
  ]);

  return null;
};

// Global locale reference that can be updated from any EvaProvider instance
let globalLanguageFromLocaleRef: { current: string } | null = null;
let acceptLanguageInterceptorRegistered = false;

const EvaProvider = ({
  children,
  configuration,
  organization,
  authenticationToken: initialAuthenticationToken,
}: PropsWithChildren<EvaProviderProps>) => {
  const endpoint = useMemo(getEndpoint, []);
  const { language: languageFromLocale = '' } = useLocaleParams();

  const [orderId, setOrderId] = useCookieStorageState('orderId', null);

  const [authenticationToken, setAuthenticationToken] = useCookieStorageState(
    'evaAuthenticationToken',
    initialAuthenticationToken,
  );

  /* Initialize global locale ref and register interceptor once */
  useEffect(() => {
    if (!globalLanguageFromLocaleRef) {
      globalLanguageFromLocaleRef = { current: languageFromLocale ?? '' };

      if (!acceptLanguageInterceptorRegistered) {
        addRequestInterceptor(async (request: Request) => {
          if (globalLanguageFromLocaleRef) {
            request.headers.set(
              'Accept-Language',
              globalLanguageFromLocaleRef.current,
            );
          }
          return request;
        });
        acceptLanguageInterceptorRegistered = true;
      }
    }
  }, [languageFromLocale]);

  /* Update global locale ref when locale changes */
  useEffect(() => {
    if (globalLanguageFromLocaleRef) {
      globalLanguageFromLocaleRef.current = languageFromLocale ?? '';
    }
  }, [languageFromLocale]);

  /* Sync EVA with the selected language (only for EVA countries) */
  useEffect(() => {
    if (!organization) return;
    const language = organization.languageId.split('-').at(0);
    if (language) {
      setCoreSetting('language', language);
    }
  }, [organization]);

  /* Sync EVA with the selected organization (only for EVA countries) */
  useEffect(() => {
    if (organization) {
      setServiceSetting(
        'requestedOrganizationUnitID',
        organization.organizationId,
      );
    }
  }, [organization]);

  const value = useMemo(
    () => ({
      endpoint,
      configuration,
      organization,
      orderId,
      setOrderId,
      authenticationToken,
      setAuthenticationToken,
    }),
    [
      endpoint,
      configuration,
      organization,
      orderId,
      setOrderId,
      authenticationToken,
      setAuthenticationToken,
    ],
  );

  return (
    <EvaContext.Provider value={value}>
      <OrderHandler />
      {children}
    </EvaContext.Provider>
  );
};

export default EvaProvider;
